import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useCurrentSubscription } from "@/features/subscription/hooks";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { CallForwardingSection } from "../components/CallForwardingSection";
import { PhoneNumberSection } from "../components/PhoneNumberSection";
import { usePhoneNumbers } from "../hooks/phoneNumberQueries";

const header = <PageHeader title="Phone number" description="Your business number and where its calls ring." />;

function PhoneNumberLoader({ businessId }: { businessId: string }) {
  const query = usePhoneNumbers();
  const subscription = useCurrentSubscription().data;
  if (!query.data) return <Page>{header}<PageContent>{query.error
    ? <ErrorState title="Couldn't load your phone number" message={query.error.message} onRetry={() => void query.refetch()} />
    : <LoadingSpinner message="Loading phone number..." />}</PageContent></Page>;
  const phone = query.data.find((item) => item.status === "active") ?? query.data[0];
  if (!phone) return <Page>{header}<PageContent><PhoneNumberSection /></PageContent></Page>;
  return <CallForwardingSection key={phone.id} header={header} businessId={businessId} phone={phone}
    locked={!!subscription && !subscription.features.includes("sms")} />;
}

export function PhoneNumberPage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();
  if (!loading && business && businessId && user && business.ownerId === user.id) {
    return <PhoneNumberLoader key={`${user.id}:${businessId}`} businessId={businessId} />;
  }
  return (
    <Page>
      {header}
      <PageContent>
        {loading ? <LoadingSpinner message="Loading business..." /> : !business || !businessId
          ? <ErrorState message="Select a business to manage its phone number." />
          : <ErrorState message="Only the business owner can manage the phone number." />}
      </PageContent>
    </Page>
  );
}
